import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Facebook, Instagram, Youtube } from "lucide-react";
import { toast } from "sonner";
import { SCHOOL } from "../lib/site";
import { subscribeNewsletter } from "../lib/api";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      setLoading(true);
      await subscribeNewsletter(email);
      toast.success("Iscrizione alla newsletter completata");
      setEmail("");
    } catch (err) {
      toast.error("Errore. Riprova più tardi.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer
      data-testid="site-footer"
      className="bg-[#A3133A] text-[#FBFBF9] mt-24 relative overflow-hidden grain"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14 py-20 grid grid-cols-1 md:grid-cols-12 gap-12 relative z-10">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3 mb-5">
            <img
              src={SCHOOL.logo}
              alt="logo"
              className="h-14 w-14 object-cover rounded-sm border border-white/30"
            />
            <div>
              <div className="font-serif-display text-2xl">Trinity Musical School</div>
              <div className="text-[10px] tracking-eyebrow opacity-80">
                Amici per la Musica APS
              </div>
            </div>
          </div>
          <p className="text-sm opacity-85 leading-relaxed max-w-md">
            {SCHOOL.subtitle}. Scuola di musica e spettacolo certificata
            Trinity College London — Centre Registration {SCHOOL.trinityCentreId}.
          </p>
          <div className="flex gap-3 mt-6">
            <a href={SCHOOL.social.facebook} aria-label="Facebook" data-testid="social-facebook" className="p-2 border border-white/30 rounded-sm hover:bg-white/10 transition">
              <Facebook size={16} />
            </a>
            <a href={SCHOOL.social.instagram} aria-label="Instagram" data-testid="social-instagram" className="p-2 border border-white/30 rounded-sm hover:bg-white/10 transition">
              <Instagram size={16} />
            </a>
            <a href={SCHOOL.social.youtube} aria-label="YouTube" data-testid="social-youtube" className="p-2 border border-white/30 rounded-sm hover:bg-white/10 transition">
              <Youtube size={16} />
            </a>
          </div>
        </div>

        <div className="md:col-span-3">
          <div className="text-[11px] tracking-eyebrow opacity-70 mb-4">Esplora</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/chi-siamo" className="link-underline opacity-90 hover:opacity-100">Chi Siamo</Link></li>
            <li><Link to="/corsi" className="link-underline opacity-90 hover:opacity-100">Corsi</Link></li>
            <li><Link to="/insegnanti" className="link-underline opacity-90 hover:opacity-100">Insegnanti</Link></li>
            <li><Link to="/eventi" className="link-underline opacity-90 hover:opacity-100">Eventi e Saggi</Link></li>
            <li><Link to="/progetti" className="link-underline opacity-90 hover:opacity-100">Progetti</Link></li>
            <li><Link to="/certificazioni" className="link-underline opacity-90 hover:opacity-100">Trinity College London</Link></li>
            <li><Link to="/iscrizione" className="link-underline opacity-90 hover:opacity-100">Iscrizione online</Link></li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <div className="text-[11px] tracking-eyebrow opacity-70 mb-4">Contatti</div>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-3">
              <Mail size={14} className="mt-1 shrink-0 opacity-80" />
              <a href={`mailto:${SCHOOL.email}`} data-testid="footer-email" className="link-underline opacity-90">
                {SCHOOL.email}
              </a>
            </li>
            {SCHOOL.phones.map((p, i) => (
              <li key={p.value} className="flex items-start gap-3" data-testid={`footer-phone-${i}`}>
                <Phone size={14} className="mt-1 shrink-0 opacity-80" />
                <span className="opacity-90">
                  <span className="block text-[10px] tracking-eyebrow opacity-70">{p.label}</span>
                  <a href={`tel:${p.tel}`} className="link-underline">{p.value}</a>
                </span>
              </li>
            ))}
            {SCHOOL.locations.map((loc) => (
              <li key={loc.id} className="flex items-start gap-3" data-testid={`footer-location-${loc.id}`}>
                <MapPin size={14} className="mt-1 shrink-0 opacity-80" />
                <span className="opacity-90">
                  <span className="block text-[10px] tracking-eyebrow opacity-70">{loc.name}</span>
                  <span>{loc.city} · {loc.address}</span>
                </span>
              </li>
            ))}
          </ul>

          <form onSubmit={handleSubscribe} className="mt-8" data-testid="footer-newsletter-form">
            <div className="text-[11px] tracking-eyebrow opacity-70 mb-3">Newsletter</div>
            <div className="flex gap-2">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="La tua email"
                data-testid="footer-newsletter-email"
                className="flex-1 bg-transparent border border-white/30 px-3 py-2 text-sm rounded-sm placeholder:text-white/50 focus:outline-none focus:border-white"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="footer-newsletter-submit"
                className="bg-white text-[#A3133A] px-4 py-2 text-[12px] tracking-eyebrow rounded-sm hover:bg-white/90 disabled:opacity-60"
              >
                {loading ? "..." : "Iscrivi"}
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="border-t border-white/15 relative z-10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14 py-6 flex flex-col md:flex-row justify-between items-center gap-3 text-[11px] opacity-70">
          <span>© {new Date().getFullYear()} {SCHOOL.org} · CF/P.IVA {SCHOOL.vatNumber} · Tutti i diritti riservati</span>
          <span>Centro Esami Trinity College London · Registration {SCHOOL.trinityCentreId}</span>
        </div>
      </div>
    </footer>
  );
}
